import { IComment } from "@/@types/comment";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useUpdateCommentMutation
} from "@/services/comment";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "components/ui/alert-dialog";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { ScrollArea } from "components/ui/scroll-area";
import { Textarea } from "components/ui/textarea";
import { toast } from "components/ui/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertTriangle, ChevronDown, ChevronUp, Clock, Edit, MessageCircle, Trash2, User } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

const CONSTANTS = {
  COMMENT_TRUNCATE_LENGTH: 200,
  TOAST_DURATION: 3000,
  MAX_COMMENT_DISPLAY: 99,
  SPINNER_SIZE: 8,
  LOADING_HEIGHT: 32,
  MAX_COMMENT_LENGTH: 1000,
  TEXTAREA_MIN_HEIGHT: 20,
  COMMENTS_MAX_HEIGHT: 96,
} as const;

interface CommentsModalProps {
  divergenceId: number;
  divergenceName: string;
  isClosed: boolean;
}

export function CommentsModal({ divergenceId, divergenceName, isClosed: isClosedProp }: CommentsModalProps) {
  const { handleModal } = useDialogModal();
  const [newComment, setNewComment] = useState("");
  const [editingComment, setEditingComment] = useState<{ id: number; content: string; version: number } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<number | null>(null);
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());

  const { data: commentsResponse, isLoading, refetch } = useGetCommentsQuery({ divergence_id: divergenceId });
  const [createComment, { isLoading: isCreating }] = useCreateCommentMutation();
  const [updateComment, { isLoading: isUpdating }] = useUpdateCommentMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

  const comments = useMemo(() => commentsResponse?.comments || [], [commentsResponse?.comments]);
  const isClosed = useMemo(() => {
    if (!commentsResponse?.conciliation_status) return isClosedProp;
    return commentsResponse.conciliation_status.toUpperCase() === "CLOSED";
  }, [commentsResponse?.conciliation_status, isClosedProp]);
  const canCreateComment = commentsResponse?.can_create_comment;

  const toggleCommentExpansion = useCallback((commentId: number) => {
    setExpandedComments(prev => {
      const newExpanded = new Set(prev);
      newExpanded.has(commentId) ? newExpanded.delete(commentId) : newExpanded.add(commentId);
      return newExpanded;
    });
  }, []);

  const truncateText = useCallback((text: string, maxLength = CONSTANTS.COMMENT_TRUNCATE_LENGTH) =>
    text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`, []);

  const showToast = useCallback((variant: "default" | "destructive", description: string) =>
    toast({ variant, description, duration: CONSTANTS.TOAST_DURATION }), []);

  const formatDate = useCallback((dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      return "Data inválida";
    }
  }, []);

  const handleSubmitNewComment = useCallback(async () => {
    if (!newComment.trim()) return showToast("destructive", "O comentário não pode estar vazio");
    try {
      await createComment({ divergence_id: divergenceId, content: newComment.trim() }).unwrap();
      setNewComment("");
      showToast("default", "Comentário adicionado com sucesso");
      refetch();
    } catch (error) {
      showToast("destructive", "Erro ao adicionar comentário");
    }
  }, [newComment, createComment, divergenceId, showToast, refetch]);

  const handleUpdateComment = useCallback(async () => {
    if (!editingComment?.content.trim()) return showToast("destructive", "O comentário não pode estar vazio");
    try {
      await updateComment({
        id: editingComment.id,
        content: editingComment.content.trim(),
        version: editingComment.version
      }).unwrap();
      setEditingComment(null);
      showToast("default", "Comentário atualizado com sucesso");
      refetch();
    } catch (error) {
      showToast("destructive", "Erro ao atualizar comentário");
    }
  }, [editingComment, updateComment, showToast, refetch]);

  const handleDeleteComment = useCallback(async () => {
    if (!commentToDelete) return;
    try {
      await deleteComment({ id: commentToDelete }).unwrap();
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
      showToast("default", "Comentário excluído com sucesso");
      refetch();
    } catch (error) {
      showToast("destructive", "Erro ao excluir comentário");
    }
  }, [commentToDelete, deleteComment, showToast, refetch]);

  const openDeleteDialog = useCallback((commentId: number) => {
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  }, []);

  const closeModal = useCallback(() => handleModal({ isOpen: false }), [handleModal]);

  return (
    <div className="w-full overflow-hidden relative">
      {isClosed && (
        <div className="absolute top-2 right-2 z-10 group">
          <div className="relative">
            <div className="p-1 rounded-full bg-amber-100 hover:bg-amber-200 transition-colors duration-200" role="button" tabIndex={0} aria-label="Informações sobre conciliação fechada">
              <AlertTriangle className="w-5 h-5 text-amber-600 hover:text-amber-700 cursor-help transition-colors duration-200" />
            </div>
            <div className="absolute top-10 right-0 w-72 sm:w-80 p-4 bg-white border border-gray-200 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-out z-20 transform translate-y-2 group-hover:translate-y-0" role="tooltip" aria-hidden="true">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Conciliação Fechada</h4>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">Esta divergência pertence a uma conciliação fechada. Não é possível adicionar, editar ou excluir comentários.</p>
                </div>
              </div>
              <div className="absolute -top-2 right-6 w-4 h-4 bg-white border-l border-t border-gray-200 transform rotate-45 shadow-sm"></div>
            </div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <p className="text-sm text-gray-600 break-words">Divergência: <span className="font-medium break-all">{divergenceName}</span></p>
        <p className="text-xs text-gray-500 mt-1">Total: {comments?.length || 0} comentário{comments?.length !== 1 ? 's' : ''}</p>
      </div>

      <ScrollArea className="h-96 mb-6">
        <div className="space-y-4 pr-2">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="flex flex-col items-center justify-center">
                <svg
                  className="animate-spin h-12 w-12 text-primary mb-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nenhum comentário ainda</p>
            </div>
          ) : (
            comments.map((comment: IComment) => {
              const commentId = comment.id || `legacy-${comment.divergence_id}-${comment.created_at}`;
              const isExpanded = comment.id ? expandedComments.has(comment.id) : false;
              const shouldTruncate = comment.content.length > CONSTANTS.COMMENT_TRUNCATE_LENGTH;

              return (
                <Card key={commentId} className="relative w-full overflow-hidden">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-[1fr_auto] gap-4 items-start mb-3">
                      <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-1 min-w-0">
                          <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          <span className="font-medium text-sm truncate">
                            {comment.author.name}
                          </span>
                        </div>
                        {comment.is_legacy && (
                          <Badge variant="secondary" className="text-xs flex-shrink-0">
                            Legado
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                          <Clock className="h-3 w-3 flex-shrink-0" />
                          {formatDate(comment.created_at)}
                        </div>
                        {!comment.is_legacy && !isClosed && comment.can_edit && comment.id && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              className="h-6 w-6 p-0 flex-shrink-0 flex items-center justify-center bg-transparent hover:bg-transparent border-none outline-none cursor-pointer focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                              onClick={() => comment.id && setEditingComment({ id: comment.id, content: comment.content, version: comment.version })}
                              aria-label={`Editar comentário de ${comment.author.name}`}
                            >
                              <Edit className="h-3 w-3 text-gray-600 hover:text-black" />
                            </button>
                            <button
                              type="button"
                              className="h-6 w-6 p-0 flex-shrink-0 flex items-center justify-center bg-transparent hover:bg-transparent border-none outline-none cursor-pointer focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded"
                              onClick={() => comment.id && openDeleteDialog(comment.id)}
                              aria-label={`Excluir comentário de ${comment.author.name}`}
                            >
                              <Trash2 className="h-3 w-3 text-red-500 hover:text-red-700" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {editingComment?.id === comment.id ? (
                      <div className="space-y-3">
                        <Textarea
                          value={editingComment.content}
                          onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })}
                          className="min-h-20 resize-none w-full"
                          maxLength={CONSTANTS.MAX_COMMENT_LENGTH}
                          placeholder="Editar comentário..."
                          aria-label="Campo para editar comentário"
                          aria-describedby={`edit-char-count-${comment.id}`}
                        />
                        <div className="flex gap-2 justify-end">
                          <span
                            id={`edit-char-count-${comment.id}`}
                            className="text-xs text-gray-500 mr-auto"
                            aria-live="polite"
                          >
                            {editingComment.content.length}/{CONSTANTS.MAX_COMMENT_LENGTH}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingComment(null)}
                            disabled={isUpdating}
                            aria-label="Cancelar edição do comentário"
                          >
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleUpdateComment}
                            isLoading={isUpdating}
                            aria-label="Salvar alterações do comentário"
                          >
                            Salvar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative w-full overflow-hidden">
                        <div
                          className={`text-sm text-gray-700 ${isExpanded ? "max-h-60 overflow-y-auto" : "max-h-20 overflow-hidden"
                            }`}
                          style={{ wordBreak: "break-all", overflowWrap: "break-word" }}
                        >
                          {shouldTruncate && !isExpanded ? truncateText(comment.content) : comment.content}
                        </div>
                        {shouldTruncate && comment.id && (
                          <div className="w-full flex justify-end mt-2">
                            <button
                              type="button"
                              className="h-6 w-6 flex items-center justify-center bg-transparent hover:bg-transparent border-none outline-none cursor-pointer focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                              onClick={() => comment.id && toggleCommentExpansion(comment.id)}
                              aria-label={isExpanded ? "Recolher comentário" : "Expandir comentário"}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? <ChevronUp className="h-4 w-4 text-black" /> : <ChevronDown className="h-4 w-4 text-black" />}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {comment.updated_at !== comment.created_at && (
                      <p className="text-xs text-gray-400 mt-2">Editado em {formatDate(comment.updated_at)}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>

      {!isClosed && canCreateComment !== false ? (
        <div className="space-y-3">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Escreva um novo comentário..."
            className="min-h-20 resize-none w-full"
            maxLength={CONSTANTS.MAX_COMMENT_LENGTH}
            aria-label="Campo para novo comentário"
            aria-describedby="char-count"
          />
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <span
              id="char-count"
              className="text-xs text-gray-500"
              aria-live="polite"
            >
              {newComment.length}/{CONSTANTS.MAX_COMMENT_LENGTH} caracteres
            </span>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="ghost"
                onClick={closeModal}
                className="flex-1 sm:flex-none hover:bg-transparent text-gray-600 hover:text-black"
                aria-label="Fechar modal de comentários"
              >
                Fechar
              </Button>
              <Button
                onClick={handleSubmitNewComment}
                isLoading={isCreating}
                disabled={!newComment.trim()}
                className="flex-1 sm:flex-none"
                aria-label="Adicionar novo comentário"
              >
                Adicionar
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-center pt-4">
          <Button variant="ghost" onClick={closeModal} className="hover:bg-transparent text-gray-600 hover:text-black">Fechar</Button>
        </div>
      )}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="z-[9999] bg-white border border-gray-200 shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">Tem certeza que deseja excluir este comentário? Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)} className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteComment} className="bg-red-600 hover:bg-red-700 text-white border-red-600" disabled={isDeleting}>{isDeleting ? "Excluindo..." : "Excluir"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
